import React, { Component } from 'react';
import ReactModal from 'react-modal';
import Loader from 'react-loader-spinner';
import {
    fetchInstallationClosureReasons,
    fetchInstallationCsvTemplate,
    fetchInstallationDetail,
    submitInstallationAction,
    updateInstallationSource,
    verifyInstallationPayment,
} from '../../actions/installationAction';
import {
    COMMENT_ACTION,
    DETAIL_TABS,
    SOURCE_OPTIONS,
    SOURCE_WITH_CHANNEL,
    actionLabel,
    ageStatusLabel,
    buildActionBody,
    formatAge,
    formatAmount,
    formatDate,
    formatDateTime,
    groupHistoryByTab,
    isLogOnlyAction,
    sourceLabel,
    splitActions,
    validateActionForm,
} from './homeAudioUtils';

const EMPTY_FORM = { comments: '', closure_reason_id: '', scheduled_date: '', duplicate_of_request_id: '' };

// Object form on purpose: a string className is APPENDED to ReactModal__Content, which
// index.css forces into a centred 560px card with !important. Objects replace it.
const DRAWER_CLASS = { base: 'ha-drawer', afterOpen: 'ha-drawer--open', beforeClose: 'ha-drawer--closing' };
const DRAWER_OVERLAY_CLASS = { base: 'ha-drawer-ov', afterOpen: 'ha-drawer-ov--open', beforeClose: 'ha-drawer-ov--closing' };

const joinParts = (parts) => parts.filter(Boolean).join(', ') || '—';

class HomeAudioRequestDrawer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            detail: null,
            isLoading: false,
            error: null,
            activeTab: 'product',
            activeAction: null,
            form: { ...EMPTY_FORM },
            commentDraft: '',
            submitting: false,
            // formError belongs to one form at a time: the open action form or the comment box.
            formError: null,
            formErrorFor: null,
            notice: null,
            closureReasons: [],
            // §5.3 source correction, inline on the Product tab.
            sourceEditing: false,
            sourceDraft: '',
            channelDraft: '',
            channels: [],
            sourceSaving: false,
            sourceError: null,
            // §7.3 Verify Payment Now.
            verifying: false,
        };
    }

    startSourceEdit = async () => {
        const { detail, channels } = this.state;
        this.setState({ sourceEditing: true, sourceDraft: detail.source || '', channelDraft: detail.channel_id ? String(detail.channel_id) : '', sourceError: null });
        if (channels.length === 0) {
            // Same eligible list the import modal shows: Amazon, Flipkart, Offline only.
            const result = await fetchInstallationCsvTemplate();
            if (result.success) this.setState({ channels: (result.data && result.data.channels) || [] });
        }
    };

    saveSource = async () => {
        const { sourceDraft, channelDraft } = this.state;
        this.setState({ sourceSaving: true, sourceError: null });
        const result = await updateInstallationSource({
            request_id: Number(this.props.requestId),
            source: sourceDraft,
            channel_id: sourceDraft === SOURCE_WITH_CHANNEL && channelDraft ? Number(channelDraft) : null,
        });
        if (!result.success) {
            this.setState({ sourceSaving: false, sourceError: result.error });
            return;
        }
        this.setState({ sourceSaving: false, sourceEditing: false, notice: result.message || 'Source updated.' }, () => {
            this.loadDetail();
            if (result.data && result.data.changed && this.props.onActionDone) this.props.onActionDone();
        });
    };

    verifyPayment = async () => {
        this.setState({ verifying: true, notice: null });
        const result = await verifyInstallationPayment(this.props.requestId);
        if (!result.success) {
            this.setState({ verifying: false, notice: result.error });
            return;
        }
        this.setState({ verifying: false, notice: result.message || 'Payment checked.' }, () => {
            this.loadDetail();
            if (result.data && result.data.checked && this.props.onActionDone) this.props.onActionDone();
        });
    };

    renderSourceEditor = (detail) => {
        const { sourceEditing, sourceDraft, channelDraft, channels, sourceSaving, sourceError } = this.state;
        if (!sourceEditing) {
            return (
                <div className="ha-dr-value-row">
                    {detail.source_label || sourceLabel(detail.source)}
                    <button className="ha-link" onClick={this.startSourceEdit} title="Correct a mis-tagged source">
                        Edit
                    </button>
                </div>
            );
        }
        return (
            <div className="ha-dr-inline-form">
                <select className="ha-select" value={sourceDraft} onChange={(e) => this.setState({ sourceDraft: e.target.value, sourceError: null })}>
                    {SOURCE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
                {sourceDraft === SOURCE_WITH_CHANNEL && (
                    <select className="ha-select" value={channelDraft} onChange={(e) => this.setState({ channelDraft: e.target.value, sourceError: null })}>
                        <option value="">Select channel…</option>
                        {channels.map((channel) => (
                            <option key={channel.id} value={channel.id}>
                                {channel.name}
                            </option>
                        ))}
                    </select>
                )}
                {sourceError && <div className="ha-error ha-error-inline">{sourceError}</div>}
                <div className="ha-dr-form-actions">
                    <button className="ha-btn ha-btn-primary" disabled={sourceSaving} onClick={this.saveSource}>
                        {sourceSaving ? 'Saving…' : 'Save'}
                    </button>
                    <button className="ha-btn ha-btn-ghost" disabled={sourceSaving} onClick={() => this.setState({ sourceEditing: false, sourceError: null })}>
                        Cancel
                    </button>
                </div>
            </div>
        );
    };

    componentDidMount() {
        this.loadDetail();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.requestId !== this.props.requestId) {
            this.setState(
                { activeTab: 'product', activeAction: null, form: { ...EMPTY_FORM }, commentDraft: '', notice: null, formError: null, sourceEditing: false },
                this.loadDetail
            );
        }
    }

    loadDetail = async () => {
        this.setState({ isLoading: true, error: null });
        const result = await fetchInstallationDetail(this.props.requestId);
        if (result.success) {
            this.setState({ detail: result.data, isLoading: false });
        } else {
            this.setState({ error: result.error, isLoading: false });
        }
    };

    // Closure reasons are only needed for the close form, so they are fetched on first open.
    handleActionSelect = async (action) => {
        if (!action || this.state.activeAction === action) {
            this.setState({ activeAction: null, formError: null });
            return;
        }
        this.setState({ activeAction: action, form: { ...EMPTY_FORM }, formError: null, notice: null });
        if (action === 'close' && this.state.closureReasons.length === 0) {
            const result = await fetchInstallationClosureReasons();
            if (result.success) {
                this.setState({ closureReasons: result.data || [] });
            } else {
                this.setState({ formError: result.error, formErrorFor: action });
            }
        }
    };

    handleFormChange = (field, value) => {
        this.setState((prev) => ({ form: { ...prev.form, [field]: value }, formError: null }));
    };

    // One submit path for moves, call logs and comments. Only a bucket move refreshes the list.
    submitAction = async (action, form) => {
        const validationError = validateActionForm(action, form);
        if (validationError) {
            this.setState({ formError: validationError, formErrorFor: action });
            return;
        }
        this.setState({ submitting: true, formError: null });
        const result = await submitInstallationAction(buildActionBody(this.props.requestId, action, form));
        if (!result.success) {
            this.setState({ submitting: false, formError: result.error, formErrorFor: action });
            return;
        }
        const historyOnly = isLogOnlyAction(action) || action === COMMENT_ACTION;
        this.setState(
            {
                submitting: false,
                activeAction: null,
                form: { ...EMPTY_FORM },
                commentDraft: action === COMMENT_ACTION ? '' : this.state.commentDraft,
                notice: (result.data && result.data.message) || 'Request updated.',
            },
            () => {
                this.loadDetail();
                if (!historyOnly && this.props.onActionDone) {
                    this.props.onActionDone();
                }
            }
        );
    };

    handleSubmit = () => this.submitAction(this.state.activeAction, this.state.form);

    handleCommentSubmit = () => this.submitAction(COMMENT_ACTION, { comments: this.state.commentDraft });

    renderError = (forAction) => {
        const { formError, formErrorFor } = this.state;
        return formError && formErrorFor === forAction ? <div className="ha-error ha-error-inline">{formError}</div> : null;
    };

    renderGrid = (rows) => (
        <div className="ha-dr-grid">
            {rows.map(([label, value]) => (
                <div className="ha-dr-cell" key={label}>
                    <span className="ha-dr-label">{label}</span>
                    <div className="ha-dr-value">{value}</div>
                </div>
            ))}
        </div>
    );

    // Bucket movement lives in one dropdown; its resting value is the current bucket.
    renderMove = (detail) => {
        const { bucketActions } = splitActions(detail.allowed_actions);
        const { activeAction } = this.state;
        const selected = activeAction && !isLogOnlyAction(activeAction) ? activeAction : '';
        return (
            <div className="ha-dr-move">
                <div className="ha-dr-move-head">
                    <span className="ha-dr-move-label">Action</span>
                    {detail.ageing && (
                        <span className={`ha-dr-move-hint ha-age-${detail.ageing.status}`}>
                            {formatAge(detail.ageing)} in bucket · {ageStatusLabel(detail.ageing.status)}
                        </span>
                    )}
                </div>
                <select
                    className="ha-select"
                    value={selected}
                    disabled={bucketActions.length === 0}
                    onChange={(e) => this.handleActionSelect(e.target.value)}
                >
                    <option value="">{detail.bucket_name || 'Unpaid — no bucket'}</option>
                    {bucketActions.map((action) => (
                        <option key={action} value={action}>
                            {actionLabel(action)}
                        </option>
                    ))}
                </select>
                {bucketActions.length === 0 && (
                    <span className="ha-dr-move-hint">
                        {detail.bucket_name ? 'This request is finished and cannot be moved.' : 'Unpaid bookings cannot be moved until payment confirms.'}
                    </span>
                )}
                {selected && this.renderActionForm()}
            </div>
        );
    };

    renderActionForm = () => {
        const { activeAction, form, submitting, closureReasons } = this.state;
        if (!activeAction) {
            return null;
        }
        return (
            <div className="ha-dr-form">
                <div className="ha-dr-form-title">{actionLabel(activeAction)}</div>

                {activeAction === 'close' && (
                    <label className="ha-dr-field">
                        <span>Closure reason</span>
                        <select
                            className="ha-select"
                            value={form.closure_reason_id}
                            onChange={(e) => this.handleFormChange('closure_reason_id', e.target.value)}
                        >
                            <option value="">Select a reason</option>
                            {closureReasons.map((reason) => (
                                <option key={reason.id} value={reason.id}>
                                    {reason.reason}
                                </option>
                            ))}
                        </select>
                    </label>
                )}

                {activeAction === 'assign' && (
                    <label className="ha-dr-field">
                        <span>Scheduled date (optional)</span>
                        <input
                            className="ha-input"
                            type="date"
                            value={form.scheduled_date}
                            onChange={(e) => this.handleFormChange('scheduled_date', e.target.value)}
                        />
                    </label>
                )}

                {activeAction === 'mark_duplicate' && (
                    <label className="ha-dr-field">
                        <span>Duplicate of request id (optional)</span>
                        <input
                            className="ha-input"
                            type="text"
                            placeholder="e.g. 10234"
                            value={form.duplicate_of_request_id}
                            onChange={(e) => this.handleFormChange('duplicate_of_request_id', e.target.value)}
                        />
                    </label>
                )}

                <label className="ha-dr-field">
                    <span>Note{isLogOnlyAction(activeAction) ? '' : ' (optional)'}</span>
                    <textarea
                        className="ha-textarea"
                        rows="2"
                        value={form.comments}
                        onChange={(e) => this.handleFormChange('comments', e.target.value)}
                        placeholder="Added to the request history"
                    />
                </label>

                {this.renderError(activeAction)}

                <div className="ha-dr-form-actions">
                    <button className="ha-btn ha-btn-primary" disabled={submitting} onClick={this.handleSubmit}>
                        {submitting ? 'Saving…' : 'Confirm'}
                    </button>
                    <button className="ha-btn ha-btn-ghost" disabled={submitting} onClick={() => this.handleActionSelect(null)}>
                        Cancel
                    </button>
                </div>
            </div>
        );
    };

    renderTabs = (groups) => {
        const { activeTab } = this.state;
        return (
            <div className="ha-dr-tabs" role="tablist">
                {DETAIL_TABS.map((tab) => {
                    const count = groups[tab.key] ? groups[tab.key].length : null;
                    return (
                        <button
                            key={tab.key}
                            role="tab"
                            aria-selected={activeTab === tab.key}
                            className={`ha-dr-tab ${activeTab === tab.key ? 'ha-dr-tab--on' : ''}`}
                            onClick={() => this.setState({ activeTab: tab.key })}
                        >
                            {tab.label}
                            {count !== null && <span className="ha-dr-tab-count">{count}</span>}
                        </button>
                    );
                })}
            </div>
        );
    };

    renderProductTab = (detail) => {
        const rows = [
            ['Product', detail.product_model || '—'],
            ['SKU', detail.sku || '—'],
            ['Order', detail.order_number || detail.order_id || '—'],
            ['Invoice', detail.invoice_number || '—'],
            ['Purchased', detail.purchase_date ? formatDate(detail.purchase_date) : '—'],
            ['Source', this.renderSourceEditor(detail)],
            ['Payment', this.renderPaymentCell(detail)],
            ['To collect', formatAmount(detail.amount_to_collect)],
            ['Scheduled', detail.scheduled_date ? formatDate(detail.scheduled_date) : 'Not scheduled'],
            ['Created', formatDateTime(detail.created_at)],
            ['Exported', detail.exported_at ? `${formatDateTime(detail.exported_at)} (batch ${detail.export_batch_id})` : 'Not exported'],
        ];
        if (detail.duplicate_of_request_id) {
            rows.push(['Duplicate of', `#${detail.duplicate_of_request_id}`]);
        }
        if (detail.closure_reason) {
            rows.push(['Closure reason', detail.closure_reason]);
        }
        return (
            <div className="ha-dr-pane">
                {this.renderGrid(rows)}
                {!detail.is_manual && (
                    <>
                        <div className="ha-dr-section-title">Payment attempts</div>
                        {this.renderPayments(detail.payments)}
                    </>
                )}
            </div>
        );
    };

    // Only a support-page booking is paid through Cashfree; the other sources assert payment.
    renderPaymentCell = (detail) => {
        const { verifying } = this.state;
        if (detail.is_manual) {
            // The marketplace collected the fee before this row existed; there is no payment to show.
            return <div className="ha-dr-value-row">Manual request — prepaid on {detail.source_label || 'channel'}</div>;
        }
        const canVerify = detail.source === 'support_page' && detail.payment_status !== 'paid';
        return (
            <div className="ha-dr-value-row">
                {`${detail.payment_status || '—'} · ${formatAmount(detail.amount)}`}
                {canVerify && (
                    <button className="ha-btn ha-btn-log ha-btn-verify" disabled={verifying} onClick={this.verifyPayment} title="Ask Cashfree right now instead of waiting for the reconcile job">
                        <i className="fas fa-sync-alt" /> {verifying ? 'Checking…' : 'Verify Payment Now'}
                    </button>
                )}
            </div>
        );
    };

    renderCustomerTab = (detail) => {
        const rows = [
            ['Customer', detail.customer_name || '—'],
            ['Mobile', detail.mobile || '—'],
            ['Email', detail.email || '—'],
            ['Address', joinParts([detail.address, detail.address_2])],
            ['City / State', joinParts([detail.city_name, detail.state_name])],
            ['Pincode', detail.pincode || '—'],
            ['Serviceable', detail.is_serviceable === null ? 'Not checked' : detail.is_serviceable ? 'Yes' : 'No'],
        ];
        return (
            <div className="ha-dr-pane">
                {this.renderGrid(rows)}
                {detail.is_mobile_masked && (
                    <div className="ha-dr-hint">Mobile is masked for agents. The full number is only in the admin Jeeves export.</div>
                )}
            </div>
        );
    };

    renderConversationsTab = (detail, entries) => {
        const { logActions } = splitActions(detail.allowed_actions);
        const { activeAction } = this.state;
        return (
            <div className="ha-dr-pane">
                {logActions.length > 0 && (
                    <div className="ha-dr-btn-row">
                        {logActions.map((action) => (
                            <button
                                key={action}
                                className={`ha-btn ha-btn-log ${activeAction === action ? 'ha-btn-action--on' : ''}`}
                                onClick={() => this.handleActionSelect(action)}
                            >
                                <i className="fas fa-phone-alt" /> {actionLabel(action)}
                            </button>
                        ))}
                    </div>
                )}
                {activeAction && isLogOnlyAction(activeAction) && this.renderActionForm()}
                {entries.length === 0 ? (
                    <div className="ha-dr-empty">No customer communication logged yet.</div>
                ) : (
                    this.renderTimeline(entries)
                )}
            </div>
        );
    };

    renderCommentsTab = (entries) => {
        const { commentDraft, submitting } = this.state;
        return (
            <div className="ha-dr-pane">
                <div className="ha-dr-compose">
                    <textarea
                        className="ha-textarea"
                        rows="3"
                        value={commentDraft}
                        onChange={(e) => this.setState({ commentDraft: e.target.value, formError: null })}
                        placeholder="Internal note — not visible to the customer"
                    />
                    {this.renderError(COMMENT_ACTION)}
                    <div className="ha-dr-compose-actions">
                        <button className="ha-btn ha-btn-primary" disabled={submitting} onClick={this.handleCommentSubmit}>
                            {submitting ? 'Saving…' : 'Add Comment'}
                        </button>
                    </div>
                </div>
                {entries.length === 0 ? (
                    <div className="ha-dr-empty">No internal comments yet.</div>
                ) : (
                    entries.map((entry) => (
                        <div className="ha-comment" key={entry.id}>
                            <div className="ha-comment-head">
                                <span className="ha-comment-author">{entry.user_name || 'System'}</span>
                                <span>{formatDateTime(entry.created_at)}</span>
                            </div>
                            <div className="ha-comment-text">{entry.comments}</div>
                        </div>
                    ))
                )}
            </div>
        );
    };

    renderPayments = (payments) => {
        if (!payments || payments.length === 0) {
            return <div className="ha-dr-empty">No payment attempts recorded.</div>;
        }
        return (
            <div className="ha-table-wrap">
                <table className="ha-table ha-table-compact">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Status</th>
                            <th>Amount</th>
                            <th>Mode</th>
                            <th>Reference</th>
                            <th>Time</th>
                        </tr>
                    </thead>
                    <tbody>
                        {payments.map((payment) => (
                            <tr key={payment.id}>
                                <td>{payment.attempt_no}</td>
                                <td>
                                    <span className={`ha-badge ha-pay-${payment.status}`}>{payment.status}</span>
                                </td>
                                <td>{formatAmount(payment.amount)}</td>
                                <td>{payment.payment_mode || '—'}</td>
                                <td className="ha-mono">{payment.reference_id || '—'}</td>
                                <td>{formatDateTime(payment.trans_time || payment.created_at)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    renderTimeline = (history) => {
        if (!history || history.length === 0) {
            return <div className="ha-dr-empty">No activity yet.</div>;
        }
        return (
            <ul className="ha-timeline">
                {history.map((entry) => (
                    <li className="ha-timeline-item" key={entry.id}>
                        <div className="ha-timeline-head">
                            <span className="ha-timeline-action">{actionLabel(entry.action)}</span>
                            <span className="ha-timeline-time">{formatDateTime(entry.created_at)}</span>
                        </div>
                        {entry.new_bucket_name && entry.new_bucket_id !== entry.old_bucket_id && (
                            <div className="ha-timeline-move">
                                {entry.old_bucket_name || 'Unbucketed'} <i className="fas fa-arrow-right" /> {entry.new_bucket_name}
                            </div>
                        )}
                        {entry.comments && <div className="ha-timeline-comment">{entry.comments}</div>}
                        <div className="ha-timeline-user">{entry.user_name || 'System'}</div>
                    </li>
                ))}
            </ul>
        );
    };

    renderBody = (detail) => {
        const { activeTab, notice } = this.state;
        const groups = groupHistoryByTab(detail.history);
        return (
            <>
                {notice && <div className="ha-notice">{notice}</div>}
                {this.renderMove(detail)}
                {this.renderTabs(groups)}
                {activeTab === 'product' && this.renderProductTab(detail)}
                {activeTab === 'customer' && this.renderCustomerTab(detail)}
                {activeTab === 'conversations' && this.renderConversationsTab(detail, groups.conversations)}
                {activeTab === 'comments' && this.renderCommentsTab(groups.comments)}
                {activeTab === 'history' && this.renderTimeline(groups.history)}
            </>
        );
    };

    render() {
        const { onClose } = this.props;
        const { detail, isLoading, error } = this.state;

        return (
            <ReactModal
                isOpen
                onRequestClose={onClose}
                className={DRAWER_CLASS}
                overlayClassName={DRAWER_OVERLAY_CLASS}
                ariaHideApp={false}
            >
                <div className="ha-dr-head">
                    <div className="ha-dr-title">
                        <span>{(detail && detail.request_no) || 'Request'}</span>
                        {detail && (
                            <span className="ha-badge ha-badge-bucket">{detail.bucket_name || 'Unpaid — no bucket'}</span>
                        )}
                    </div>
                    <button className="ha-dr-close" onClick={onClose} title="Close">
                        <i className="fas fa-times" />
                    </button>
                </div>

                <div className="ha-dr-body">
                    {isLoading && !detail ? (
                        <div className="ha-loader">
                            <Loader type="Oval" color="Grey" height={60} width={60} />
                        </div>
                    ) : error ? (
                        <div className="ha-error">{error}</div>
                    ) : detail ? (
                        this.renderBody(detail)
                    ) : null}
                </div>

                <div className="ha-dr-foot">
                    <button className="ha-btn ha-btn-ghost" onClick={onClose}>
                        Close
                    </button>
                </div>
            </ReactModal>
        );
    }
}

export default HomeAudioRequestDrawer;
